import { mapPaginatedResponse } from '@/mappers/common.mapper';
import {
  mapBatchCallCreatePayload,
  mapBatchCallDetailResponse,
  mapBatchCallUpdatePayload,
  mapBatchRecipientResponse,
  mapRecipientUpdatePayload,
  mapScheduleResponse,
} from '@/mappers/scheduler.mapper';

import type {
  BatchCallFormValues,
  BatchCallUpdateFormValues,
  RecipientUpdateFormValues,
} from '@/schemas/batch-call.schema';

import apiClient from '@/lib/api-client';
import { raiseAxiosErrorOrDefault } from '@/lib/error';
import { buildQueryParams } from '@/lib/utils';

import type { PaginatedResponse, PaginationParams } from '@/types/common.type';
import type {
  BatchCall,
  BatchCallDetail,
  BatchCallFilterParams,
  BatchRecipient,
} from '@/types/scheduler.type';

export const fetchBatchCalls = async (
  params: BatchCallFilterParams = { page: 1, page_size: 10 }
): Promise<PaginatedResponse<BatchCall>> => {
  try {
    const response = await apiClient.get<PaginatedResponse<BatchCall>>(
      '/scheduled-tasks',
      mapPaginatedResponse(mapScheduleResponse),
      {
        params: buildQueryParams(params, {
          ...(params.filters && { status: params.filters.status }),
        }),
      }
    );

    return response;
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to fetch batch calls');
  }
};

export const createBatchCall = async (values: BatchCallFormValues): Promise<void> => {
  try {
    const payload = mapBatchCallCreatePayload(values);
    await apiClient.post<void>('/scheduled-tasks', payload);
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to create batch call');
  }
};

export const deleteBatchCall = async (id: number): Promise<void> => {
  try {
    await apiClient.delete(`/scheduled-tasks/${id}`);
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to delete batch call');
  }
};

export const updateBatchCall = async (
  id: number,
  values: BatchCallUpdateFormValues
): Promise<void> => {
  try {
    const payload = mapBatchCallUpdatePayload(values);
    await apiClient.patch<BatchCall>(`/scheduled-tasks/${id}`, payload);
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to update batch call');
  }
};

export const fetchBatchCallDetail = async (id: number): Promise<BatchCallDetail> => {
  try {
    const response = await apiClient.get<BatchCallDetail>(
      `/scheduled-tasks/${id}`,
      mapBatchCallDetailResponse
    );
    return response;
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to fetch batch call detail');
  }
};

export const fetchBatchCallRecipients = async (
  id: number,
  params: PaginationParams = { page: 1, page_size: 10 }
): Promise<PaginatedResponse<BatchRecipient>> => {
  try {
    const response = await apiClient.get<PaginatedResponse<BatchRecipient>>(
      `/scheduled-tasks/${id}/recipients`,
      mapPaginatedResponse(mapBatchRecipientResponse),
      {
        params: {
          page: params.page || 1,
          page_size: params.page_size || 10,
        },
      }
    );
    return response;
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to fetch recipients');
  }
};

export const updateBatchCallRecipient = async (
  batchCallId: number,
  recipientId: number,
  values: RecipientUpdateFormValues
): Promise<void> => {
  try {
    const payload = mapRecipientUpdatePayload(values);
    await apiClient.patch<void>(
      `/scheduled-tasks/${batchCallId}/recipients/${recipientId}`,
      payload
    );
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to update recipient');
  }
};

export const deleteBatchCallRecipient = async (
  batchCallId: number,
  recipientId: number
): Promise<void> => {
  try {
    await apiClient.delete(`/scheduled-tasks/${batchCallId}/recipients/${recipientId}`);
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to delete recipient');
  }
};

export const downloadSampleTemplate = async (agentId: string): Promise<Blob> => {
  try {
    const blob = await apiClient.get<Blob>(
      `/scheduled-tasks/agents/${agentId}/download-template`,
      undefined,
      { responseType: 'blob' }
    );
    return blob;
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to download sample template');
  }
};
